import React, { useState } from 'react';
import { FileText, Brain, Copy, Check } from 'lucide-react';
import { PDFAnalysisData } from '../types';

interface ResultDisplayProps {
  data: PDFAnalysisData | null;
  type: 'analysis';
  isLoading: boolean;
}

const ResultDisplay: React.FC<ResultDisplayProps> = ({ data, type, isLoading }) => {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  if (isLoading) {
    return (
      <div className="card">
        <div className="flex items-center justify-center space-x-2">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
          <span className="text-gray-600 dark:text-gray-300">
            Analyzing PDF content...
          </span>
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const content = (data as PDFAnalysisData).analysis;

  return (
    <div className="space-y-6">
      {/* File Info */}
      <div className="card">
        <div className="flex items-center space-x-3">
          <FileText className="h-8 w-8 text-primary-500" />
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{data.fileName}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Size: {(data.fileSize / 1024 / 1024).toFixed(2)} MB
            </p>
          </div>
        </div>
      </div>

      {/* AI Analysis */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Brain className="h-6 w-6 text-primary-500" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              AI Analysis
            </h3>
          </div>
          <button
            onClick={() => copyToClipboard(content)}
            className="flex items-center space-x-1 text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
          >
            {copied ? (
              <Check className="h-4 w-4 text-green-500" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
            <span>{copied ? 'Copied!' : 'Copy'}</span>
          </button>
        </div>
        
        <div className="prose max-w-none">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <pre className="whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-200 font-sans">
              {content}
            </pre>
          </div>
        </div>

        {/* Cache Information */}
        {(data as PDFAnalysisData).isFromCache && (
          <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <p className="text-sm text-green-800 dark:text-green-200">
              <strong>📋 Cached Result:</strong> This analysis was retrieved from cache (no OpenAI tokens used)
            </p>
          </div>
        )}

        {/* Usage Information */}
        {(data as PDFAnalysisData).usage && (
          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              <strong>Token Usage:</strong> {(data as PDFAnalysisData).usage!.total_tokens} tokens 
              (Prompt: {(data as PDFAnalysisData).usage!.prompt_tokens}, Completion: {(data as PDFAnalysisData).usage!.completion_tokens})
            </p>
          </div>
        )}
      </div>

    </div>
  );
};

export default ResultDisplay;
